/**
 * DroidShop — buying from someone else's VENDOR droid (2026-09-24). K at a droid whose cargo carries
 * prices opens it: each priced line, a quantity and Buy. The server checks the range (12 m), the price
 * and the stock (buyFromDroid); 95 % goes to the owner, 5 % to the platform.
 */
import { makeWindow, type UiWindow } from '../../lib/ui_window.js';
import { atlas } from '../../state.js';
import { bus } from '../../bus.js';
import { sfx } from '../../lib/sfx.js';
import { credits, shopTotal } from '../../lib/droid_view.js';

export class DroidShop {
  private win: UiWindow | null = null;
  private droidId: bigint | null = null;
  private timer: number | null = null;
  /** The shelf's signature (the rows only — the wallet line is patched in place, see `render`). */
  private sig = '';
  /** Quantities the buyer typed, per entry: a rebuild (a sale, a re-price) keeps them (2026-09-25). */
  private qtyDraft = new Map<string, string>();

  isOpen(): boolean { return !!this.win?.isOpen(); }
  current(): bigint | null { return this.isOpen() ? this.droidId : null; }

  open(droidId: bigint): void {
    if (!this.win) {
      this.win = makeWindow({ id: 'droid-shop', title: 'Droid stall', icon: 'droid', width: 420, height: 380, minWidth: 320, minHeight: 200 });
      this.win.root.id = 'droid-shop';
      this.timer = window.setInterval(() => { if (this.isOpen()) this.render(false); }, 1000);
    }
    if (this.droidId !== droidId) this.qtyDraft.clear(); // another stall: its own quantities
    this.droidId = droidId;
    this.win.setOpen(true);
    this.render(true);
  }

  close(): void { this.win?.setOpen(false); }

  dispose(): void {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.win?.root.remove();
  }

  private render(force: boolean): void {
    const c = atlas.conn;
    if (!this.win || !c || this.droidId === null) return;
    const d = c.db.droidUnit.droidId.find(this.droidId);
    if (!d) { this.close(); return; }
    const rows = [...c.db.droidCargo.droidCargoByDroid.filter(this.droidId)].filter((e) => e.priceSov > 0n);
    const wallet = c.identity ? c.db.wallet.identity.find(c.identity)?.sov ?? 0n : 0n;
    const body = this.win.body;
    // SOFT RENDER (2026-09-25): the wallet was part of the signature, so every credit the buyer earned or spent anywhere
    // rebuilt the stall under their cursor — the quantity box lost its focus and its value. The wallet line is patched
    // in place; the shelf is rebuilt only when it changed, never while a quantity box has the focus (the next tick,
    // after the blur), and the scroll position, the typed quantities and the focused control survive it.
    const headText = `For sale · your wallet ${credits(Number(wallet))} credits`;
    const walletEl = body.querySelector<HTMLElement>('[data-role="shop-wallet"]');
    if (walletEl && walletEl.textContent !== headText) walletEl.textContent = headText;
    const sig = rows.map((e) => `${e.entryId}:${e.qty}:${e.priceSov}`).join(',');
    if (!force && sig === this.sig && walletEl) return;
    const active = document.activeElement as HTMLElement | null;
    const inside = !!active && body.contains(active);
    if (!force && inside && (active!.tagName === 'INPUT' || active!.tagName === 'SELECT' || active!.tagName === 'TEXTAREA')) return;
    this.sig = sig;
    const focusKey = inside ? active!.dataset.k ?? null : null;
    const scrollTop = body.scrollTop;
    const owner = c.db.account.identity.find(d.owner);
    this.win.setTitle(`${d.name} — ${owner ? (owner.displayName || owner.username) : 'a traveler'}'s stall`);
    body.textContent = '';
    const wrap = document.createElement('div');
    wrap.style.cssText = 'padding:10px 12px;font:13px/1.45 var(--ax-font);color:var(--ax-text)';
    const head = document.createElement('div');
    head.className = 'ax-head';
    head.dataset.role = 'shop-wallet';
    head.textContent = headText;
    wrap.appendChild(head);
    if (rows.length === 0) {
      const p = document.createElement('p');
      p.style.color = 'var(--ax-muted)';
      p.textContent = 'Nothing priced for sale right now.';
      wrap.appendChild(p);
    }
    for (const e of rows) {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:8px;align-items:center;margin:6px 0';
      const name = document.createElement('span');
      name.style.flex = '1';
      name.textContent = `${e.kind === 'item' ? `${e.name} (q${e.quality})` : e.name} — ${e.qty} at ${e.priceSov} ${e.kind === 'stack' ? 'each' : ''}`;
      const qty = document.createElement('input');
      qty.className = 'ax-input ax-num';
      qty.type = 'number'; qty.min = '1'; qty.max = String(e.qty); qty.value = String(Math.min(e.qty, 10));
      qty.style.width = '70px';
      const ek = e.entryId.toString();
      qty.dataset.k = `q:${ek}`;
      const typed = this.qtyDraft.get(ek);
      if (typed !== undefined) qty.value = typed; // what the buyer typed before the shelf changed
      if (e.kind === 'item') qty.style.display = 'none';
      const total = document.createElement('span');
      total.className = 'ax-num';
      const upd = (): void => { total.textContent = `${shopTotal(e.priceSov, e.kind === 'item' ? 1 : Number(qty.value) || 0)} cr`; };
      qty.oninput = () => { this.qtyDraft.set(ek, qty.value); upd(); };
      qty.onkeydown = (ev) => ev.stopPropagation();
      upd();
      const buy = document.createElement('button');
      buy.className = 'ax-btn is-primary';
      buy.textContent = 'Buy';
      buy.dataset.k = `b:${ek}`;
      buy.onclick = () => {
        sfx('sfx_droid_beep', { gain: 0.35, cooldownMs: 150 });
        const q = e.kind === 'item' ? 1 : Math.max(1, Math.min(e.qty, Math.floor(Number(qty.value) || 1)));
        // THE PRICE YOU SAW (2026-09-25): the shelf can be re-priced between the look and the click (the owner from
        // anywhere, or the droid's own program every tick) — the server refuses unless it still asks this price.
        c.reducers.buyFromDroid({ droidId: d.droidId, entryId: e.entryId, qty: q, expectPrice: e.priceSov })
          .then(() => { this.qtyDraft.delete(ek); bus.emit('atlas:toast', { text: `Bought ${q} × ${e.name}`, kind: 'success', ms: 2500 }); })
          .catch((err: Error) => {
            bus.emit('atlas:toast', { text: String(err?.message ?? err).replace(/^SenderError:\s*/, ''), kind: 'error' });
            this.sig = ''; // a refused price: show the shelf as it stands now on the next tick
          });
      };
      row.append(name, qty, total, buy);
      wrap.appendChild(row);
    }
    body.appendChild(wrap);
    body.scrollTop = scrollTop;
    if (focusKey) body.querySelector<HTMLElement>(`[data-k="${focusKey}"]`)?.focus({ preventScroll: true });
  }
}
